// DEBT-VITA™ — Formulaire de saisie des paramètres de la dette
// Brief : 4 sections (A/B/C/D), pré-remplissage automatique depuis les PDFs
// analysés du projet, validations strictes, persistance via PUT /api/debt/{id}/parameters.
//
// ⚠️  Ce composant ne déclenche AUCUN moteur de calcul (volontaire — Phase DV-2).
//     Le bouton "Calculer le poids vitaliste" affiche un toast informatif.

import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Save,
  RotateCcw,
  Calculator,
  Plus,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import DebtFieldHelp from "@/components/debt/DebtFieldHelp";
import {
  getDebtPrefill,
  getDebtParameters,
  saveDebtParameters,
  CREDITOR_TYPES,
  CURRENCY_OPTIONS,
} from "@/lib/debt";

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------
function num(v) {
  if (v === "" || v === null || v === undefined) return null;
  const s = String(v).replace(/\s/g, "").replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

const REQUIRED_KEYS = [
  "total_public_debt",
  "annual_debt_service",
  "annual_fiscal_deficit",
  "total_population",
  "active_population",
];

const DEFAULTS = {
  total_public_debt: "",
  domestic_debt: "",
  external_debt: "",
  debt_to_gdp_ratio: "",
  annual_debt_service: "",
  interest_payments: "",
  principal_repayments: "",
  average_interest_rate: "",
  average_maturity_years: "",
  annual_fiscal_deficit: "",
  gdp_total: "",
  total_population: "",
  active_population: "",
  youth_population: "",
  projection_years: 30,
  gdp_growth_rate: "3.5",
  population_growth_rate: "2.8",
  expected_deficit_trend: "0",
  data_source: "",
  fiscal_year: new Date().getFullYear(),
  currency: "UM",
  creditors: [],
};

// ----------------------------------------------------------------------------
// Sub-component : Field with help tooltip
// ----------------------------------------------------------------------------
function NumberField({
  id,
  label,
  help,
  suffix,
  value,
  onChange,
  required,
  prefilled,
  warning,
  step = "any",
  min = 0,
  testId,
  placeholder,
}) {
  return (
    <div>
      <Label
        htmlFor={id}
        className="flex items-center text-[11px] font-semibold uppercase tracking-widest text-muted-foreground"
      >
        <span>
          {label}
          {required ? <span className="ml-1 text-red-600">*</span> : null}
        </span>
        {help ? <DebtFieldHelp id={id} {...help} /> : null}
        {prefilled ? (
          <span
            className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
            data-testid={`debt-prefilled-${id}`}
          >
            <CheckCircle2 className="h-3 w-3" />
            Importé du budget
          </span>
        ) : null}
      </Label>
      <div className="relative mt-1">
        <Input
          id={id}
          type="number"
          step={step}
          min={min}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 pr-16"
          placeholder={placeholder}
          data-testid={testId || `debt-field-${id}`}
        />
        {suffix ? (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
            {suffix}
          </span>
        ) : null}
      </div>
      {warning ? (
        <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-400">
          <AlertTriangle className="h-3 w-3" />
          {warning}
        </p>
      ) : null}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Main component
// ----------------------------------------------------------------------------
export default function DebtParametersForm({
  projectId,
  onCalculateClicked,
}) {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefillSource, setPrefillSource] = useState(null);
  const [prefilledKeys, setPrefilledKeys] = useState({}); // {key: true}
  const [accordionOpen, setAccordionOpen] = useState(["A", "B", "C"]);

  // Initial load: parameters (already saved) OR prefill from documents
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const existing = await getDebtParameters(projectId);
        if (existing) {
          if (cancelled) return;
          setForm({
            ...DEFAULTS,
            ...existing,
            creditors: (existing.creditors || []).map((c) => ({
              creditor_name: c.creditor_name || "",
              creditor_type: c.creditor_type || "MULTILATERAL",
              amount: c.amount ?? "",
              interest_rate: c.interest_rate ?? "",
              maturity_years: c.maturity_years ?? "",
              currency_loan: c.currency_loan || "USD",
            })),
            fiscal_year: existing.fiscal_year ?? DEFAULTS.fiscal_year,
            currency: existing.currency || "UM",
            projection_years: existing.projection_years || 30,
          });
          setPrefilledKeys({});
        } else {
          await applyPrefill(true);
        }
      } catch (e) {
        toast.error("Impossible de charger les paramètres dette.");
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  async function applyPrefill(initial = false) {
    try {
      const pre = await getDebtPrefill(projectId);
      if (!pre) return;
      setPrefillSource(pre);
      const updates = {};
      const flags = {};
      for (const k of [
        "total_public_debt",
        "annual_fiscal_deficit",
        "gdp_total",
        "fiscal_year",
      ]) {
        if (pre[k] !== null && pre[k] !== undefined && pre[k] !== "") {
          updates[k] = pre[k];
          flags[k] = true;
        }
      }
      if (Object.keys(updates).length === 0) {
        if (!initial) toast.info("Aucune donnée à importer depuis les PDFs.");
        return;
      }
      setForm((f) => ({ ...f, ...updates }));
      setPrefilledKeys((prev) => ({ ...prev, ...flags }));
      if (!initial) {
        toast.success(
          `Réinitialisé depuis ${pre.source_doc_count || 0} PDF(s) analysés.`
        );
      }
    } catch (e) {
      toast.error("Erreur lors du pré-remplissage.");
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
    if (prefilledKeys[key]) {
      setPrefilledKeys((p) => {
        const n = { ...p };
        delete n[key];
        return n;
      });
    }
  }

  // -------- Auto-calc helpers --------
  function autoCalcDebtToGdp() {
    const debt = num(form.total_public_debt);
    const gdp = num(form.gdp_total);
    if (debt && gdp && gdp > 0) {
      setField("debt_to_gdp_ratio", ((debt / gdp) * 100).toFixed(2));
    } else {
      toast.info("Saisir d'abord la dette totale et le PIB.");
    }
  }

  function autoFillTotalDebt() {
    const dom = num(form.domestic_debt);
    const ext = num(form.external_debt);
    if (dom !== null && ext !== null) {
      setField("total_public_debt", (dom + ext).toFixed(2));
    } else {
      toast.info("Renseigner d'abord les dettes intérieure et extérieure.");
    }
  }

  function estimateActivePop() {
    const total = num(form.total_population);
    if (total && total > 0) {
      setField("active_population", Math.round(total * 0.4));
      toast.success("Estimation appliquée : 40 % de la population totale.");
    } else {
      toast.info("Renseigner d'abord la population totale.");
    }
  }

  // -------- Creditors --------
  function addCreditor() {
    setForm((f) => ({
      ...f,
      creditors: [
        ...(f.creditors || []),
        {
          creditor_name: "",
          creditor_type: "MULTILATERAL",
          amount: "",
          interest_rate: "",
          maturity_years: "",
          currency_loan: "USD",
        },
      ],
    }));
  }

  function updateCreditor(idx, key, value) {
    setForm((f) => {
      const next = [...(f.creditors || [])];
      next[idx] = { ...next[idx], [key]: value };
      return { ...f, creditors: next };
    });
  }

  function removeCreditor(idx) {
    setForm((f) => ({
      ...f,
      creditors: (f.creditors || []).filter((_, i) => i !== idx),
    }));
  }

  // -------- Validations --------
  const validation = useMemo(() => {
    const errors = [];
    const warnings = {};

    const missingRequired = REQUIRED_KEYS.filter((k) => num(form[k]) === null);

    const tot = num(form.total_public_debt);
    const dom = num(form.domestic_debt);
    const ext = num(form.external_debt);
    if (tot !== null && dom !== null && ext !== null) {
      if (dom + ext > tot * 1.05) {
        errors.push(
          "Dette intérieure + extérieure dépasse la dette totale (>5 % de tolérance)."
        );
      }
    }

    const svc = num(form.annual_debt_service);
    const interest = num(form.interest_payments);
    const principal = num(form.principal_repayments);
    if (svc !== null && interest !== null && principal !== null) {
      if (interest + principal > svc * 1.05) {
        errors.push(
          "Intérêts + remboursements en capital dépassent le service annuel (>5 %)."
        );
      }
    }

    const popTotal = num(form.total_population);
    const popActive = num(form.active_population);
    if (popTotal !== null && popActive !== null && popActive > popTotal) {
      errors.push("Population active supérieure à la population totale.");
    }

    const intRate = num(form.average_interest_rate);
    if (intRate !== null && intRate > 50) {
      errors.push("Taux d'intérêt moyen invalide (>50 %).");
    } else if (intRate !== null && intRate > 15) {
      warnings.average_interest_rate = "Taux anormalement élevé (>15 %).";
    }

    // Créanciers : somme vs total
    const sumCred = (form.creditors || []).reduce(
      (acc, c) => acc + (num(c.amount) || 0),
      0
    );
    const creditorWarning =
      tot && sumCred > 0 && Math.abs(sumCred - tot) / tot > 0.05
        ? `Écart de ${(((sumCred - tot) / tot) * 100).toFixed(1)} % entre la somme des créanciers et la dette totale.`
        : null;

    return {
      errors,
      warnings,
      missingRequired,
      sumCred,
      creditorWarning,
      canCalculate: errors.length === 0 && missingRequired.length === 0,
    };
  }, [form]);

  // -------- Persist --------
  function buildPayload() {
    const payload = {};
    for (const key of Object.keys(DEFAULTS)) {
      if (key === "creditors") continue;
      const raw = form[key];
      if (raw === "" || raw === null || raw === undefined) continue;
      if (
        ["projection_years", "fiscal_year", "total_population", "active_population", "youth_population"].includes(
          key
        )
      ) {
        const n = parseInt(raw, 10);
        if (!Number.isNaN(n)) payload[key] = n;
      } else if (key === "currency" || key === "data_source") {
        payload[key] = String(raw);
      } else {
        const n = num(raw);
        if (n !== null) payload[key] = n;
      }
    }
    payload.creditors = (form.creditors || [])
      .filter((c) => c.creditor_name && num(c.amount) !== null)
      .map((c) => ({
        creditor_name: String(c.creditor_name).trim(),
        creditor_type: c.creditor_type,
        amount: num(c.amount) || 0,
        interest_rate: num(c.interest_rate),
        maturity_years: num(c.maturity_years),
        currency_loan: c.currency_loan ? String(c.currency_loan).toUpperCase() : null,
      }));
    return payload;
  }

  async function handleSave() {
    if (validation.errors.length > 0) {
      toast.error(validation.errors[0]);
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const saved = await saveDebtParameters(projectId, payload);
      if (saved) {
        toast.success("Paramètres dette sauvegardés.");
        setPrefilledKeys({});
      }
    } catch (e) {
      const msg =
        e?.response?.data?.detail || e?.message || "Échec de la sauvegarde.";
      toast.error(String(msg));
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  function handleCalculate() {
    if (!validation.canCalculate) {
      toast.error(
        "Renseignez d'abord les champs obligatoires et corrigez les erreurs."
      );
      return;
    }
    if (typeof onCalculateClicked === "function") {
      onCalculateClicked(buildPayload());
    } else {
      toast.info(
        "Moteur de calcul — Phase DV-2 (en attente d'activation)",
        { duration: 5000 }
      );
    }
  }

  // ----------------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------------
  if (loading) {
    return (
      <Card data-testid="debt-form-loading">
        <CardContent className="p-6 text-sm text-muted-foreground">
          Chargement des paramètres dette…
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="debt-parameters-form">
      {/* Pre-fill banner */}
      {prefillSource && Object.keys(prefilledKeys).length > 0 ? (
        <div
          className="flex items-start gap-3 rounded-lg border border-emerald-300/60 bg-emerald-50 p-3 text-sm dark:border-emerald-700/50 dark:bg-emerald-950/30"
          data-testid="debt-prefill-banner"
        >
          <Sparkles className="mt-0.5 h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          <div className="text-emerald-900 dark:text-emerald-200">
            <strong>{Object.keys(prefilledKeys).length} valeur(s)</strong> pré-remplie(s)
            depuis vos PDFs analysés
            {prefillSource.source_doc_count
              ? ` (${prefillSource.source_doc_count} document(s)).`
              : "."} Vous pouvez les modifier librement.
          </div>
        </div>
      ) : null}

      <Accordion
        type="multiple"
        value={accordionOpen}
        onValueChange={setAccordionOpen}
        className="space-y-4"
      >
        {/* SECTION A — Données macroéconomiques */}
        <AccordionItem
          value="A"
          className="overflow-hidden rounded-xl border border-border bg-card"
        >
          <AccordionTrigger
            className="px-4 py-3 text-sm font-semibold hover:no-underline"
            data-testid="debt-section-A-trigger"
          >
            A. Données Macroéconomiques de la Dette
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-5 pt-2">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <NumberField
                id="total_public_debt"
                label="Dette publique totale"
                suffix={form.currency}
                required
                prefilled={prefilledKeys.total_public_debt}
                value={form.total_public_debt}
                onChange={(v) => setField("total_public_debt", v)}
                help={{
                  title: "Dette publique totale",
                  description:
                    "Total de la dette intérieure + extérieure de l'État, en monnaie nationale (ou devise du projet).",
                }}
              />
              <div className="flex items-end gap-2">
                <NumberField
                  id="domestic_debt"
                  label="Dont : Dette intérieure"
                  suffix={form.currency}
                  value={form.domestic_debt}
                  onChange={(v) => setField("domestic_debt", v)}
                  help={{
                    title: "Dette intérieure",
                    description:
                      "Part de la dette détenue par des créanciers résidents (banques nationales, marché obligataire interne).",
                  }}
                />
              </div>
              <NumberField
                id="external_debt"
                label="Dont : Dette extérieure"
                suffix={form.currency}
                value={form.external_debt}
                onChange={(v) => setField("external_debt", v)}
                help={{
                  title: "Dette extérieure",
                  description:
                    "Part détenue par des créanciers non-résidents : multilatéraux (FMI, BM), bilatéraux, marchés internationaux.",
                }}
              />
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={autoFillTotalDebt}
                  data-testid="debt-autofill-total"
                >
                  ⇡ Total = Intérieure + Extérieure
                </Button>
              </div>

              <NumberField
                id="debt_to_gdp_ratio"
                label="Ratio dette/PIB"
                suffix="%"
                value={form.debt_to_gdp_ratio}
                onChange={(v) => setField("debt_to_gdp_ratio", v)}
                help={{
                  title: "Ratio dette / PIB",
                  description:
                    "Indicateur international clé. Maastricht : ≤ 60 %. Au-delà de 90 %, zone d'alerte selon Reinhart-Rogoff.",
                }}
              />
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={autoCalcDebtToGdp}
                  data-testid="debt-autocalc-ratio"
                >
                  ⇡ Calculer auto (dette / PIB)
                </Button>
              </div>

              <NumberField
                id="annual_debt_service"
                label="Service annuel de la dette"
                suffix={form.currency}
                required
                value={form.annual_debt_service}
                onChange={(v) => setField("annual_debt_service", v)}
                help={{
                  title: "Service annuel de la dette",
                  description:
                    "Total versé par l'État sur l'exercice : intérêts + remboursements en capital.",
                }}
              />
              <NumberField
                id="interest_payments"
                label="Dont : Intérêts annuels"
                suffix={form.currency}
                value={form.interest_payments}
                onChange={(v) => setField("interest_payments", v)}
              />
              <NumberField
                id="principal_repayments"
                label="Dont : Remboursements capital"
                suffix={form.currency}
                value={form.principal_repayments}
                onChange={(v) => setField("principal_repayments", v)}
              />
              <NumberField
                id="average_interest_rate"
                label="Taux d'intérêt moyen"
                suffix="%"
                value={form.average_interest_rate}
                onChange={(v) => setField("average_interest_rate", v)}
                warning={validation.warnings.average_interest_rate}
                help={{
                  title: "Taux d'intérêt moyen pondéré",
                  description:
                    "Moyenne des taux des emprunts en cours, pondérée par les montants. Indicateur de coût de la dette.",
                }}
              />
              <NumberField
                id="average_maturity_years"
                label="Maturité moyenne"
                suffix="ans"
                value={form.average_maturity_years}
                onChange={(v) => setField("average_maturity_years", v)}
                help={{
                  title: "Maturité moyenne",
                  description:
                    "Durée moyenne restante des emprunts. Plus elle est courte, plus le risque de refinancement est élevé.",
                }}
              />
              <NumberField
                id="annual_fiscal_deficit"
                label="Déficit fiscal annuel"
                suffix={form.currency}
                required
                prefilled={prefilledKeys.annual_fiscal_deficit}
                value={form.annual_fiscal_deficit}
                onChange={(v) => setField("annual_fiscal_deficit", v)}
                step="any"
                min={undefined}
                help={{
                  title: "Déficit fiscal annuel",
                  description:
                    "Dépenses publiques moins recettes fiscales sur l'année. Mesure le besoin de financement annuel.",
                }}
              />
              <NumberField
                id="gdp_total"
                label="PIB total"
                suffix={form.currency}
                prefilled={prefilledKeys.gdp_total}
                value={form.gdp_total}
                onChange={(v) => setField("gdp_total", v)}
                help={{
                  title: "PIB total",
                  description:
                    "Produit intérieur brut nominal de l'année fiscale (en monnaie nationale).",
                }}
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* SECTION B — Démographie */}
        <AccordionItem
          value="B"
          className="overflow-hidden rounded-xl border border-border bg-card"
        >
          <AccordionTrigger
            className="px-4 py-3 text-sm font-semibold hover:no-underline"
            data-testid="debt-section-B-trigger"
          >
            B. Données Démographiques
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-5 pt-2">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <NumberField
                id="total_population"
                label="Population totale"
                suffix="hab."
                required
                value={form.total_population}
                onChange={(v) => setField("total_population", v)}
                step="1"
                help={{
                  title: "Population totale",
                  description:
                    "Source : recensement national ou estimation des Nations Unies (UN DESA).",
                }}
              />
              <NumberField
                id="active_population"
                label="Population active (contribuables)"
                suffix="hab."
                required
                value={form.active_population}
                onChange={(v) => setField("active_population", v)}
                step="1"
                help={{
                  title: "Population active",
                  description:
                    "Personnes en âge et en capacité de travailler et payer des impôts. Source : BIT / institut national de statistique.",
                }}
              />
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={estimateActivePop}
                  data-testid="debt-estimate-active-pop"
                >
                  Estimer (40 % de la pop. totale)
                </Button>
              </div>
              <NumberField
                id="youth_population"
                label="Population de moins de 18 ans"
                suffix="hab."
                value={form.youth_population}
                onChange={(v) => setField("youth_population", v)}
                step="1"
                help={{
                  title: "Population jeune (< 18 ans)",
                  description:
                    "Utilisée pour calculer la dette par enfant à naître et l'impact intergénérationnel (formule D6).",
                }}
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* SECTION C — Projection */}
        <AccordionItem
          value="C"
          className="overflow-hidden rounded-xl border border-border bg-card"
        >
          <AccordionTrigger
            className="px-4 py-3 text-sm font-semibold hover:no-underline"
            data-testid="debt-section-C-trigger"
          >
            C. Paramètres de Projection
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-5 pt-2">
            <div className="space-y-5">
              <div>
                <Label className="flex items-center text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Horizon de projection
                  <DebtFieldHelp
                    id="horizon"
                    title="Horizon de projection"
                    description="Nombre d'années sur lesquelles la dette est projetée. 30 ans = horizon générationnel standard (formule D6)."
                  />
                </Label>
                <div className="mt-3 flex items-center gap-4">
                  <Slider
                    min={10}
                    max={40}
                    step={10}
                    value={[Number(form.projection_years) || 30]}
                    onValueChange={([v]) => setField("projection_years", v)}
                    className="max-w-md"
                    data-testid="debt-slider-horizon"
                  />
                  <span
                    className="rounded-md bg-muted px-3 py-1 text-sm font-bold tabular-nums"
                    data-testid="debt-horizon-value"
                  >
                    {form.projection_years} ans
                  </span>
                </div>
                <div className="mt-2 flex gap-2">
                  {[10, 20, 30, 40].map((y) => (
                    <Button
                      key={y}
                      variant={Number(form.projection_years) === y ? "default" : "outline"}
                      size="sm"
                      onClick={() => setField("projection_years", y)}
                      data-testid={`debt-horizon-${y}`}
                    >
                      {y}
                    </Button>
                  ))}
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <NumberField
                  id="gdp_growth_rate"
                  label="Croissance PIB projetée"
                  suffix="%/an"
                  value={form.gdp_growth_rate}
                  onChange={(v) => setField("gdp_growth_rate", v)}
                  step="0.1"
                  min={undefined}
                  placeholder="3.5"
                  help={{
                    title: "Taux de croissance du PIB",
                    description:
                      "Hypothèse de croissance moyenne annuelle. Source recommandée : FMI World Economic Outlook.",
                  }}
                />
                <NumberField
                  id="population_growth_rate"
                  label="Croissance démographique"
                  suffix="%/an"
                  value={form.population_growth_rate}
                  onChange={(v) => setField("population_growth_rate", v)}
                  step="0.1"
                  min={undefined}
                  placeholder="2.8"
                  help={{
                    title: "Taux de croissance démographique",
                    description:
                      "Variation annuelle moyenne de la population totale. Source : UN DESA.",
                  }}
                />
                <div>
                  <Label className="flex items-center text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Tendance d'évolution du déficit
                    <DebtFieldHelp
                      id="deficit_trend"
                      title="Tendance du déficit"
                      description="Direction supposée du déficit annuel sur l'horizon de projection."
                    />
                  </Label>
                  <div className="mt-2 flex gap-2">
                    {[
                      { v: -5, l: "Réduction (-5%)" },
                      { v: 0, l: "Stable (0%)" },
                      { v: 5, l: "Aggravation (+5%)" },
                    ].map((opt) => (
                      <Button
                        key={opt.v}
                        type="button"
                        variant={
                          Number(form.expected_deficit_trend) === opt.v
                            ? "default"
                            : "outline"
                        }
                        size="sm"
                        onClick={() => setField("expected_deficit_trend", opt.v)}
                        data-testid={`debt-deficit-trend-${opt.v}`}
                      >
                        {opt.l}
                      </Button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="flex items-center text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Année fiscale
                  </Label>
                  <Input
                    type="number"
                    min={1900}
                    max={2100}
                    value={form.fiscal_year}
                    onChange={(e) => setField("fiscal_year", e.target.value)}
                    className="mt-1 h-9"
                    data-testid="debt-field-fiscal_year"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Devise
                  </Label>
                  <Select
                    value={form.currency}
                    onValueChange={(v) => setField("currency", v)}
                  >
                    <SelectTrigger
                      className="mt-1 h-9"
                      data-testid="debt-field-currency"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_OPTIONS.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Source des données
                  </Label>
                  <Input
                    type="text"
                    value={form.data_source}
                    onChange={(e) => setField("data_source", e.target.value)}
                    className="mt-1 h-9"
                    placeholder="Ex : Loi de Finances 2025, FMI Article IV, Banque Mondiale"
                    data-testid="debt-field-data_source"
                  />
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* SECTION D — Créanciers */}
        <AccordionItem
          value="D"
          className="overflow-hidden rounded-xl border border-border bg-card"
        >
          <AccordionTrigger
            className="px-4 py-3 text-sm font-semibold hover:no-underline"
            data-testid="debt-section-D-trigger"
          >
            D. Détail par Créancier (optionnel)
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-5 pt-2">
            <div className="space-y-3">
              {(form.creditors || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Aucun créancier saisi. Cette section est optionnelle.
                </p>
              ) : null}

              {(form.creditors || []).map((c, idx) => (
                <Card
                  key={idx}
                  className="border-amber-200/40"
                  data-testid={`debt-creditor-${idx}`}
                >
                  <CardContent className="grid grid-cols-1 gap-3 p-4 md:grid-cols-3">
                    <div>
                      <Label className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                        Nom du créancier
                      </Label>
                      <Input
                        type="text"
                        value={c.creditor_name}
                        onChange={(e) =>
                          updateCreditor(idx, "creditor_name", e.target.value)
                        }
                        className="mt-1 h-9"
                        placeholder="Ex : FMI, Banque Mondiale, Chine, France"
                        data-testid={`debt-creditor-${idx}-name`}
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                        Type
                      </Label>
                      <Select
                        value={c.creditor_type}
                        onValueChange={(v) =>
                          updateCreditor(idx, "creditor_type", v)
                        }
                      >
                        <SelectTrigger
                          className="mt-1 h-9"
                          data-testid={`debt-creditor-${idx}-type`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CREDITOR_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                        Montant
                      </Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          step="any"
                          min={0}
                          value={c.amount}
                          onChange={(e) =>
                            updateCreditor(idx, "amount", e.target.value)
                          }
                          className="h-9 pr-12"
                          data-testid={`debt-creditor-${idx}-amount`}
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                          {c.currency_loan || form.currency}
                        </span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                        Taux d'intérêt
                      </Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          step="0.01"
                          min={0}
                          value={c.interest_rate}
                          onChange={(e) =>
                            updateCreditor(idx, "interest_rate", e.target.value)
                          }
                          className="h-9 pr-8"
                          data-testid={`debt-creditor-${idx}-rate`}
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                          %
                        </span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                        Maturité
                      </Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          step="0.5"
                          min={0}
                          value={c.maturity_years}
                          onChange={(e) =>
                            updateCreditor(idx, "maturity_years", e.target.value)
                          }
                          className="h-9 pr-10"
                          data-testid={`debt-creditor-${idx}-maturity`}
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                          ans
                        </span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                        Devise du prêt
                      </Label>
                      <Select
                        value={c.currency_loan || "USD"}
                        onValueChange={(v) =>
                          updateCreditor(idx, "currency_loan", v)
                        }
                      >
                        <SelectTrigger
                          className="mt-1 h-9"
                          data-testid={`debt-creditor-${idx}-currency`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CURRENCY_OPTIONS.map((cur) => (
                            <SelectItem key={cur} value={cur}>
                              {cur}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-3 flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCreditor(idx)}
                        className="text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                        data-testid={`debt-creditor-${idx}-remove`}
                      >
                        <Trash2 className="mr-1 h-4 w-4" />
                        Supprimer ce créancier
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}

              <Button
                type="button"
                variant="outline"
                onClick={addCreditor}
                data-testid="debt-add-creditor"
              >
                <Plus className="mr-1 h-4 w-4" />
                Ajouter un créancier
              </Button>

              {(form.creditors || []).length > 0 ? (
                <div
                  className="rounded-md border border-border bg-muted/30 p-3 text-sm"
                  data-testid="debt-creditors-summary"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      Somme des créanciers saisis :
                    </span>
                    <span className="font-bold tabular-nums">
                      {validation.sumCred.toLocaleString("fr-FR", {
                        maximumFractionDigits: 2,
                      })}{" "}
                      {form.currency}
                    </span>
                  </div>
                  {form.total_public_debt ? (
                    <div className="mt-1 flex items-center justify-between">
                      <span className="text-muted-foreground">
                        Dette totale déclarée :
                      </span>
                      <span className="font-bold tabular-nums">
                        {Number(form.total_public_debt).toLocaleString("fr-FR", {
                          maximumFractionDigits: 2,
                        })}{" "}
                        {form.currency}
                      </span>
                    </div>
                  ) : null}
                  {validation.creditorWarning ? (
                    <p
                      className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-700 dark:text-amber-400"
                      data-testid="debt-creditor-warning"
                    >
                      <AlertTriangle className="h-3 w-3" />
                      {validation.creditorWarning}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Errors summary */}
      {validation.errors.length > 0 ? (
        <div
          className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm dark:border-red-800 dark:bg-red-950/40"
          data-testid="debt-form-errors"
        >
          <p className="font-bold text-red-800 dark:text-red-300">
            <AlertTriangle className="mr-1 inline h-4 w-4" />
            Erreurs à corriger :
          </p>
          <ul className="mt-1 list-disc pl-6 text-red-700 dark:text-red-300">
            {validation.errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {validation.missingRequired.length > 0 ? (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/40"
          data-testid="debt-form-missing"
        >
          <p className="font-semibold text-amber-800 dark:text-amber-300">
            Champs obligatoires manquants : {validation.missingRequired.length}
          </p>
        </div>
      ) : null}

      {/* Action buttons */}
      <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
        <Button
          onClick={handleSave}
          disabled={saving || validation.errors.length > 0}
          className="bg-amber-600 text-white hover:bg-amber-700"
          data-testid="debt-save-btn"
        >
          <Save className="mr-2 h-4 w-4" />
          {saving ? "Sauvegarde…" : "Sauvegarder les paramètres"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => applyPrefill(false)}
          data-testid="debt-reset-prefill-btn"
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          Réinitialiser depuis le PDF
        </Button>
        <div className="ml-auto">
          <Button
            type="button"
            variant="default"
            disabled={!validation.canCalculate}
            onClick={handleCalculate}
            className="bg-red-700 text-white hover:bg-red-800 disabled:opacity-50"
            data-testid="debt-calculate-btn"
          >
            <Calculator className="mr-2 h-4 w-4" />
            Calculer le poids vitaliste de la dette →
          </Button>
        </div>
      </div>
    </div>
  );
}
